import { usePackages } from '@consigli/hooks';
import { Button, Checkbox } from '@consigli/shadcn';
import {
  DocumentPackageType,
  Language,
  OFFER_PROCESSOR_CATEGORIES,
  ProcessorPreferenceKey,
} from '@consigli/types';
import { HiOutlinePlusSm, HiOutlineTrash } from 'react-icons/hi';

import { ConflictTypeSelector, Field, Fieldset, Select, TextInput } from '@/components';
import { FileUpload } from '@/components/file-upload';

import { type BidReviewBidEntry, createEmptyBid } from './logic/use-unified-service-form';
import { languageOptions } from './logic/utils';

type TenderPackageMode = 'existing' | 'new';

const SERVICE_RISK_INSIGHT_GAPS = 'RISK_INSIGHT_GAPS';
const SERVICE_BID_VS_BID = 'BID_VS_BID';

interface BidReviewSectionProps {
  projectId: number;
  bids: BidReviewBidEntry[];
  services: string[];
  autoFillLevelingSheet: boolean;
  levelingSheetFiles: File[];
  tenderMode: TenderPackageMode;
  existingTenderPackageId: string | null;
  tenderFiles: File[];
  selectedConflictTypes: ProcessorPreferenceKey[];
  errors?: Record<string, string>;
  onBidsChange: (bids: BidReviewBidEntry[]) => void;
  onServicesChange: (services: string[]) => void;
  onAutoFillLevelingSheetChange: (value: boolean) => void;
  onLevelingSheetFilesChange: (files: File[]) => void;
  onTenderModeChange: (mode: TenderPackageMode) => void;
  onExistingTenderPackageIdChange: (id: string | null) => void;
  onTenderFilesChange: (files: File[]) => void;
  onSelectedConflictTypesChange: (value: ProcessorPreferenceKey[]) => void;
}

const BidEntryCard = ({
  bid,
  index,
  canRemove,
  onUpdate,
  onRemove,
}: {
  bid: BidReviewBidEntry;
  index: number;
  canRemove: boolean;
  onUpdate: (updated: BidReviewBidEntry) => void;
  onRemove: () => void;
}) => (
  <div className="rounded-md border border-gray-200 p-4 space-y-3">
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-gray-700">Bid {index + 1}</span>
      {canRemove && (
        <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
          <HiOutlineTrash className="h-4 w-4 text-red-500" />
        </Button>
      )}
    </div>
    <Field label="Provider / Contractor Name" htmlFor={`bid-provider-${bid.id}`} required>
      <TextInput
        placeholder="e.g. Acme Construction"
        value={bid.providerName}
        onChange={(e) => onUpdate({ ...bid, providerName: e.target.value })}
        className="w-full"
      />
    </Field>
    <Field label="Language" htmlFor={`bid-language-${bid.id}`} required>
      <Select
        options={languageOptions}
        placeholder="Select language"
        value={languageOptions.find((opt) => opt.value === bid.language) ?? undefined}
        onValueChange={(option) => onUpdate({ ...bid, language: option.value as Language })}
        getOptionLabel={(option) => option.label}
        getOptionValue={(option) => option.value}
        className="w-full"
      />
    </Field>
    <Field label="Bid Documents" htmlFor={`bid-files-${bid.id}`} required>
      <FileUpload
        setFiles={(files) => onUpdate({ ...bid, files })}
        placeholder="Upload bid documents"
        files={bid.files}
      />
    </Field>
  </div>
);

export const BidReviewSection = ({
  projectId,
  bids,
  services,
  autoFillLevelingSheet,
  levelingSheetFiles,
  tenderMode,
  existingTenderPackageId,
  tenderFiles,
  selectedConflictTypes,
  errors = {},
  onBidsChange,
  onServicesChange,
  onAutoFillLevelingSheetChange,
  onLevelingSheetFilesChange,
  onTenderModeChange,
  onExistingTenderPackageIdChange,
  onTenderFilesChange,
  onSelectedConflictTypesChange,
}: BidReviewSectionProps) => {
  const { packages: tenderPackages, isLoading } = usePackages(
    projectId,
    DocumentPackageType.TENDER_DOCUMENTS,
  );

  const tenderPackageOptions = tenderPackages.map((pkg) => ({
    label: pkg.name,
    value: pkg.id,
  }));

  const selectedTenderOption = tenderPackageOptions.find(
    (opt) => opt.value === existingTenderPackageId,
  );

  const riskSelected = services.includes(SERVICE_RISK_INSIGHT_GAPS);
  const bidVsBidSelected = services.includes(SERVICE_BID_VS_BID);

  const showTenderSection = riskSelected || (bidVsBidSelected && !autoFillLevelingSheet);

  const toggleService = (service: string) => {
    if (services.includes(service)) {
      onServicesChange(services.filter((s) => s !== service));
    } else {
      onServicesChange([...services, service]);
    }
  };

  const handleUpdateBid = (index: number, updated: BidReviewBidEntry) => {
    const next = [...bids];
    next[index] = updated;
    onBidsChange(next);
  };

  const handleRemoveBid = (index: number) => {
    onBidsChange(bids.filter((_, i) => i !== index));
  };

  const handleAddBid = () => {
    onBidsChange([...bids, createEmptyBid()]);
  };

  return (
    <>
      {/* Bids */}
      <Fieldset legend="Bids">
        <p className="text-sm text-gray-500">
          Add one or more contractor bids to compare against the tender requirements
        </p>
        <div className="space-y-3">
          {bids.map((bid, index) => (
            <BidEntryCard
              key={bid.id}
              bid={bid}
              index={index}
              canRemove={bids.length > 1}
              onUpdate={(updated) => handleUpdateBid(index, updated)}
              onRemove={() => handleRemoveBid(index)}
            />
          ))}
        </div>
        <Button type="button" variant="outline" onClick={handleAddBid} className="w-full">
          <HiOutlinePlusSm className="mr-1 h-4 w-4" /> Add another bid
        </Button>
      </Fieldset>

      {/* Analysis Services */}
      <Fieldset legend="Analysis Services">
        <p className="text-sm text-gray-500">Select the types of analysis to run on the bids</p>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id="service-risk-insight-gaps"
              checked={riskSelected}
              onCheckedChange={() => toggleService(SERVICE_RISK_INSIGHT_GAPS)}
            />
            <label htmlFor="service-risk-insight-gaps" className="text-sm cursor-pointer">
              Risk / Insight / Gaps
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="service-bid-vs-bid"
              checked={bidVsBidSelected}
              onCheckedChange={() => toggleService(SERVICE_BID_VS_BID)}
            />
            <label htmlFor="service-bid-vs-bid" className="text-sm cursor-pointer">
              Bid vs. Bid Comparison
            </label>
          </div>
        </div>
      </Fieldset>

      {/* Risk / Insight / Gaps advanced controls */}
      {riskSelected && (
        <Fieldset legend="Analysis Controls">
          <ConflictTypeSelector
            selectedConflictTypes={selectedConflictTypes}
            onSelectionChange={onSelectedConflictTypesChange}
            label="Risks, Insights and Gaps"
            description="Choose which risks, insights and gaps to identify in the bids"
            categories={OFFER_PROCESSOR_CATEGORIES}
          />
        </Fieldset>
      )}

      {/* Leveling Sheet (when Bid vs. Bid is selected) */}
      {bidVsBidSelected && (
        <Fieldset legend="Leveling Sheet">
          <div className="flex items-center gap-2">
            <Checkbox
              id="auto-fill-leveling-sheet"
              checked={autoFillLevelingSheet}
              onCheckedChange={(checked) => onAutoFillLevelingSheetChange(checked === true)}
            />
            <label htmlFor="auto-fill-leveling-sheet" className="text-sm cursor-pointer">
              Auto-fill leveling sheet
            </label>
          </div>
          {autoFillLevelingSheet && (
            <Field label="Leveling Sheet File" htmlFor="leveling-sheet-input">
              <FileUpload
                setFiles={onLevelingSheetFilesChange}
                placeholder="Upload leveling sheet"
                files={levelingSheetFiles}
              />
            </Field>
          )}
        </Fieldset>
      )}

      {/* Tender / RFP Package */}
      {showTenderSection && (
        <Fieldset legend="Tender / RFP Package">
          <p className="text-sm text-gray-500">
            Select an existing tender package to compare against, or upload new tender documents
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={tenderMode === 'existing' ? 'default' : 'outline'}
              onClick={() => onTenderModeChange('existing')}
            >
              Use existing
            </Button>
            <Button
              type="button"
              variant={tenderMode === 'new' ? 'default' : 'outline'}
              onClick={() => onTenderModeChange('new')}
            >
              Upload new
            </Button>
          </div>

          {tenderMode === 'existing' && (
            <Field
              label="Tender Package"
              htmlFor="tender-package-select"
              required
              error={errors.existingTenderPackageId}
            >
              <Select
                options={tenderPackageOptions}
                placeholder={isLoading ? 'Loading...' : 'Select tender package'}
                value={selectedTenderOption ?? null}
                onValueChange={(option) => onExistingTenderPackageIdChange(option?.value ?? null)}
                getOptionLabel={(option) => option?.label ?? ''}
                getOptionValue={(option) => option?.value ?? ''}
                className="w-full"
              />
            </Field>
          )}

          {tenderMode === 'new' && (
            <Field label="Tender Documents" htmlFor="tender-files-input" error={errors.tenderFiles}>
              <FileUpload
                setFiles={onTenderFilesChange}
                placeholder="Upload tender documents"
                files={tenderFiles}
              />
            </Field>
          )}
        </Fieldset>
      )}
    </>
  );
};
